import { type ReactNode, useRef } from 'react'
import { Camera, Mail, Trash2 } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useUserForm } from '@/hooks/useUserForm'
import { avatarBackgroundFor } from '@/lib/avatarBackground'
import { cn } from '@/lib/utils'
import { SETTINGS_IMAGE_ACCEPT } from '@/lib/settingsImageUpload'
import {
  type PermissionMatrix as Matrix,
  type PortalUser,
  type UserRole,
  type WebRole,
} from '@/types'
import { PermissionMatrix } from './PermissionMatrix'
import {
  UserDocumentsSection,
  type PendingUserDocument,
} from './UserDocumentsSection'

export interface UserFormProps {
  user: PortalUser | null
  viewerRole: UserRole | undefined
  initialMatrix: Matrix
  onClose: () => void
  onCancel?: () => void
  // 2.11.1 View → same Create/Edit screen (2.11.2) in read-only mode.
  readOnly?: boolean
  canEdit?: boolean
  // When true, block demoting / deactivating the last active Admin.
  protectLastAdmin?: boolean
  onRequestEdit?: () => void
  canResendCredentials?: boolean
  resendPending?: boolean
  onResendCredentials?: () => void
}

const fieldInputClass =
  'h-10 rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none placeholder:font-normal placeholder:text-[#8f95a6]'

const readOnlyInputClass =
  'focus:!border-transparent focus:!ring-0 focus:!ring-offset-0 focus:!shadow-none focus-visible:!border-transparent focus-visible:!ring-0 focus-visible:!ring-offset-0 focus-visible:!shadow-none'

const selectTriggerClass = cn(
  'h-10 rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none data-[placeholder]:font-normal [&>span]:font-normal',
  FORM_CONTROL_FOCUS_CLASS
)

const sectionCardClass =
  'rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-12px_rgba(38,45,66,0.1)]'

const primaryButtonClass =
  'h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 text-sm font-medium text-white shadow-none hover:bg-[#e6511c]'

const secondaryButtonClass =
  'h-10 rounded-xl border border-[#e7e9ef] bg-white px-4 text-sm font-medium text-[#171a20] shadow-none hover:bg-[#fff8f4]'

const ghostActionClass =
  'inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border border-dashed border-[#ff5a1f] bg-white px-3 text-sm font-medium leading-none text-[#ff5a1f] transition-colors hover:bg-[#fff1ea] disabled:pointer-events-none disabled:opacity-50'

function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='flex items-center gap-1 text-xs font-medium leading-4 text-[#171a20]'
    >
      {children}
      {required && <span className='text-[#ec221f]'>*</span>}
    </Label>
  )
}

function SectionCard({
  index,
  title,
  action,
  children,
}: {
  index: number
  title: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className={sectionCardClass}>
      <header className='flex flex-wrap items-center justify-between gap-3 px-4 pb-2 pt-4 sm:px-6'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className='inline-flex size-6 shrink-0 items-center justify-center rounded-lg bg-[#ff5a1f] text-xs font-medium text-white'>
            {index}
          </span>
          <h2 className='min-w-0 font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
            {title}
          </h2>
        </div>
        {action}
      </header>
      <div className='flex flex-col gap-4 px-4 pb-4 pt-2 sm:px-6 sm:pb-6'>
        {children}
      </div>
    </section>
  )
}

// Inner form body - all state seeded lazily from props at mount time.
export function UserForm({
  user,
  viewerRole,
  initialMatrix,
  onClose,
  onCancel,
  readOnly = false,
  canEdit = false,
  protectLastAdmin = false,
  onRequestEdit,
  canResendCredentials = false,
  resendPending = false,
  onResendCredentials,
}: UserFormProps) {
  const {
    t,
    isEdit,
    isSelf,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    title,
    setTitle,
    role,
    status,
    setStatus,
    avatarPreview,
    handleAvatarChange,
    handleRemoveAvatar,
    matrix,
    errors,
    clearError,
    isSaving,
    roleOptions,
    handleRoleChange,
    handleResetMatrix,
    handleMatrixChange,
    handleSave,
    pendingDocuments,
    setPendingDocuments,
  } = useUserForm({ user, viewerRole, initialMatrix, onClose })

  const fileInput = useRef<HTMLInputElement>(null)
  const placeholder = t('users.fields.enterHere')
  const locked = readOnly || isSaving
  const documentsReadOnly = readOnly
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim()
  const avatarName = fullName || email || t('users.title')

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f4f5f8]'>
      <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-6 sm:py-6'>
        <SectionCard index={1} title={t('users.dialog.informationTitle')}>
          <div className='flex flex-col gap-2'>
            <FieldLabel>{t('users.fields.avatar')}</FieldLabel>
            {!readOnly && (
              <input
                ref={fileInput}
                type='file'
                accept={SETTINGS_IMAGE_ACCEPT}
                className='hidden'
                onChange={e => {
                  handleAvatarChange(e.target.files?.[0] ?? null)
                  e.target.value = ''
                }}
              />
            )}
            <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
              {avatarPreview ? (
                <div className='flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border border-transparent bg-[#f4f5f8 text-[#3d434e]'>
                  <img
                    src={avatarPreview}
                    alt=''
                    className='size-full object-cover'
                  />
                </div>
              ) : (
                <Avatar
                  name={fullName || null}
                  className='h-14 w-14 rounded-full border border-[#e5e7ee] bg-transparent font-heading text-2xl font-semibold text-white'
                  style={{ background: avatarBackgroundFor(avatarName) }}
                />
              )}

              {readOnly ? null : (
                <div className='min-w-0 flex-1'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <button
                      type='button'
                      className={ghostActionClass}
                      onClick={() => fileInput.current?.click()}
                      disabled={isSaving}
                    >
                      <Camera className='size-5' />
                      {t('users.fields.addPhoto')}
                    </button>
                    {avatarPreview && (
                      <button
                        type='button'
                        className='inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-[#b91c1c] bg-white px-3 text-sm font-medium leading-none text-[#991b1b] transition-colors hover:bg-[#fef2f2] disabled:pointer-events-none disabled:opacity-50'
                        onClick={handleRemoveAvatar}
                        disabled={isSaving}
                        aria-label={t('users.fields.removeAvatar')}
                      >
                        <Trash2 className='size-4' />
                        {t('users.fields.removeAvatar')}
                      </button>
                    )}
                  </div>
                  <p className='mt-1 text-xs font-medium leading-4 text-[#6c7285]'>
                    {t('users.fields.avatarHint')}
                  </p>
                  {errors.avatar && (
                    <p className='mt-1 text-sm text-destructive'>
                      {errors.avatar}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div className='flex flex-col gap-1'>
              <FieldLabel htmlFor='user-first-name' required={!readOnly}>
                {t('users.fields.firstName')}
              </FieldLabel>
              <Input
                id='user-first-name'
                value={firstName}
                maxLength={80}
                placeholder={placeholder}
                readOnly={readOnly}
                disabled={locked && !readOnly}
                onChange={e => {
                  setFirstName(e.target.value)
                  clearError('firstName')
                }}
                className={cn(
                  fieldInputClass,
                  readOnly && readOnlyInputClass,
                  readOnly && 'text-[#3d434e]',
                  errors.firstName && 'ring-1 ring-destructive'
                )}
              />
              {errors.firstName && (
                <p className='text-sm text-destructive'>{errors.firstName}</p>
              )}
            </div>

            <div className='flex flex-col gap-1'>
              <FieldLabel htmlFor='user-last-name' required={!readOnly}>
                {t('users.fields.lastName')}
              </FieldLabel>
              <Input
                id='user-last-name'
                value={lastName}
                maxLength={80}
                placeholder={placeholder}
                readOnly={readOnly}
                disabled={locked && !readOnly}
                onChange={e => {
                  setLastName(e.target.value)
                  clearError('lastName')
                }}
                className={cn(
                  fieldInputClass,
                  readOnly && readOnlyInputClass,
                  readOnly && 'text-[#3d434e]',
                  errors.lastName && 'ring-1 ring-destructive'
                )}
              />
              {errors.lastName && (
                <p className='text-sm text-destructive'>{errors.lastName}</p>
              )}
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <FieldLabel htmlFor='user-email' required={!readOnly}>
              {t('users.fields.email')}
            </FieldLabel>
            <Input
              id='user-email'
              type='email'
              value={email}
              maxLength={100}
              placeholder={placeholder}
              readOnly={readOnly}
              disabled={readOnly ? false : isSelf || isSaving}
              onChange={e => {
                setEmail(e.target.value)
                clearError('email')
              }}
              className={cn(
                fieldInputClass,
                readOnly && readOnlyInputClass,
                readOnly && 'text-[#3d434e]',
                errors.email && 'ring-1 ring-destructive'
              )}
            />
            {errors.email && (
              <p className='text-sm text-destructive'>{errors.email}</p>
            )}
          </div>

          <div className='flex flex-col gap-1'>
            <FieldLabel htmlFor='user-title'>
              {t('users.fields.title')}
            </FieldLabel>
            <Input
              id='user-title'
              value={title}
              maxLength={100}
              placeholder={placeholder}
              readOnly={readOnly}
              disabled={locked && !readOnly}
              onChange={e => setTitle(e.target.value)}
              className={cn(
                fieldInputClass,
                readOnly && readOnlyInputClass,
                readOnly && 'text-[#3d434e]'
              )}
            />
          </div>

          <div className='flex flex-col gap-1'>
            <FieldLabel required={!readOnly}>
              {t('users.fields.role')}
            </FieldLabel>
            {readOnly ? (
              <Input
                value={role ? t(`users.roles.${role}`) : '—'}
                readOnly
                className={cn(
                  fieldInputClass,
                  readOnlyInputClass,
                  'text-[#3d434e]'
                )}
              />
            ) : (
              <Select
                value={role || undefined}
                onValueChange={value => handleRoleChange(value as WebRole)}
                disabled={isSelf || protectLastAdmin}
              >
                <SelectTrigger
                  className={cn(
                    selectTriggerClass,
                    !role && 'text-[#8f95a6]',
                    errors.role && 'ring-1 ring-destructive'
                  )}
                >
                  <SelectValue
                    placeholder={t('users.fields.rolePlaceholder')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map(option => (
                    <SelectItem key={option} value={option}>
                      {t(`users.roles.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {errors.role && (
              <p className='text-sm text-destructive'>{errors.role}</p>
            )}
          </div>

          <div className='flex items-center justify-between gap-3'>
            <Label
              htmlFor='user-active'
              className='text-sm font-medium leading-[1.4] text-[#171a20]'
            >
              {t('users.fields.active')}
            </Label>
            <Switch
              id='user-active'
              checked={status === 'active'}
              disabled={readOnly || isSelf || protectLastAdmin}
              onCheckedChange={checked =>
                setStatus(checked ? 'active' : 'inactive')
              }
              className='h-6 w-10 data-[state=checked]:bg-[#21c08b] data-[state=unchecked]:bg-[#d9e2ec] [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-4 [&>span]:data-[state=unchecked]:translate-x-0.5'
            />
          </div>
        </SectionCard>

        <PermissionMatrix
          value={matrix}
          onChange={handleMatrixChange}
          onReset={handleResetMatrix}
          disabled={readOnly || isSaving || !role || isSelf}
          targetRole={role}
        />

        <UserDocumentsSection
          userId={user?.id}
          readOnly={documentsReadOnly}
          disabled={isSaving}
          pendingFiles={pendingDocuments}
          onPendingChange={(next: PendingUserDocument[]) =>
            setPendingDocuments(next)
          }
        />
      </div>

      <div className='flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-[#d9e2ec] bg-white px-4 py-6 sm:px-6'>
        {readOnly ? (
          <>
            {canResendCredentials && onResendCredentials && (
              <Button
                type='button'
                variant='outline'
                className={secondaryButtonClass}
                disabled={resendPending}
                onClick={onResendCredentials}
              >
                <Mail className='mr-2 size-4' />
                {t('users.dialog.resendCredentials')}
              </Button>
            )}
            {canEdit && onRequestEdit && (
              <Button
                type='button'
                className={primaryButtonClass}
                onClick={onRequestEdit}
              >
                {t('common:actions.edit')}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              type='button'
              variant='outline'
              className={secondaryButtonClass}
              onClick={onCancel ?? onClose}
              disabled={isSaving}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type='button'
              className={primaryButtonClass}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isEdit
                ? t('users.dialog.saveChanges')
                : t('common:actions.save')}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
